import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GEREKCE_AGIR_EN_AZ } from "@koclukpanel/contracts";
import { Alan, BosDurum, Buton, Iskelet, Kart, Rozet } from "@koclukpanel/ui";
import {
  ApiHatasi,
  girisDenemeleri,
  kilitAc,
  kilitliHesaplar,
  type GirisDenemesi,
  type IpYogunlugu,
  type KilitliHesap,
} from "./api";

/**
 * KİLİTLİ HESAPLAR VE GİRİŞ DENEMELERİ — F-SEC-02 / F-SEC-02a
 *
 * ================================================================
 * AYRI DOSYA, AYRI İZİN
 * ================================================================
 * Güvenlik konsolunun ayarlar bölümü `sistem.guvenlik.goruntule`
 * istiyor; kilitli hesap listesi `sistem.guvenlik.kilit_ac`. İkisini
 * tek bileşene gömseydim, yalnızca birini taşıyan bir rol için ekranın
 * yarısı **sessizce boş** açılırdı — bu depoda daha önce ölçülmüş bir
 * hata sınıfı.
 *
 * Bu yüzden her bölüm KENDİ isteğini atıyor ve kendi hatasını
 * gösteriyor: 403 alan bölüm "yetkiniz yok" der, ötekini etkilemez.
 */

/** Süreyi insanın okuyacağı hâle çevirir. 90 → "1 sa 30 dk". */
export function kalanMetni(dakika: number): string {
  if (dakika <= 0) return "süresi doldu";
  if (dakika < 60) return `${dakika} dk`;
  const saat = Math.floor(dakika / 60);
  const kalan = dakika % 60;
  return kalan === 0 ? `${saat} sa` : `${saat} sa ${kalan} dk`;
}

/**
 * IP satırının ne anlattığı — sayı değil CÜMLE.
 *
 * ================================================================
 * BU AYRIM EKRANIN VAR OLMA SEBEBİ
 * ================================================================
 * Kilit mekanizması KİŞİ başına sayıyor. Kırk farklı kimliği birer kez
 * deneyen bir saldırgan hiçbir hesabı kilitlemez ve mevcut korumaya
 * **hiç görünmez**. Ekran iki durumu ayırt edemezse, sistemin en büyük
 * kör noktası kör kalır.
 *
 * Eşik `3`: iki farklı kimlik, aynı ailedeki iki hesabın karıştırılması
 * kadar sıradan olabilir. Üç ve üstü, tesadüf açıklamasını zorlar.
 */
export function ipYorumu(ip: IpYogunlugu): { metin: string; tehlikeli: boolean } {
  if (ip.farkliKimlik >= 3) {
    return {
      metin: `${ip.farkliKimlik} farklı kimlik denenmiş — kimlik taraması olabilir. Bu tür deneme hiçbir hesabı kilitlemez, çünkü kilit kişi başına sayar.`,
      tehlikeli: true,
    };
  }
  if (ip.farkliKimlik === 1 && ip.deneme >= 5) {
    return {
      metin: "Tek bir hesabın şifresi deneniyor. Kilit mekanizması bunu görür.",
      tehlikeli: false,
    };
  }
  return { metin: "Dağınık deneme — çoğunlukla unutulmuş şifre.", tehlikeli: false };
}

function zaman(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

function KilitliSatir({ hesap }: { hesap: KilitliHesap }) {
  const qc = useQueryClient();
  const [gerekce, setGerekce] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState<string | null>(null);

  const ac = useMutation({
    mutationFn: () => kilitAc(hesap.userId, gerekce ?? ""),
    onSuccess: async (s) => {
      /*
        `acildi: false` = hesap zaten kilitli değilmiş (süre dolmuş,
        liste eskimiş). Bunu "açıldı" diye göstermek, olmayan bir işlemi
        yapılmış saymak olurdu — üstelik yönetici bir daha bakmazdı.
      */
      setSonuc(
        s.acildi
          ? "Kilit açıldı. Kullanıcı hemen giriş yapabilir."
          : "Hesap zaten kilitli değildi — süresi kendiliğinden dolmuş. Sayaç yine de sıfırlandı.",
      );
      setGerekce(null);
      await qc.invalidateQueries({ queryKey: ["guvenlik-kilitli"] });
    },
  });

  return (
    <div className="border-notr-200 flex flex-col gap-2 border-b py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-notr-900 text-sm font-medium">{hesap.adSoyad}</p>
          <p className="text-notr-600 mt-0.5 text-xs">{hesap.email ?? "e-posta yok"}</p>
          <p className="text-notr-600 mt-1 text-xs">
            Kilidin bitmesine <span className="tabular-nums">{kalanMetni(hesap.kalanDakika)}</span>{" "}
            kaldı
            {hesap.sonKilitAt && <> · son kilit {zaman(hesap.sonKilitAt)}</>}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {hesap.kilitSayisi > 1 && (
            /*
              TEKRARLANAN KİLİT AYRI BİR ŞEY. Birinci kilit çoğunlukla
              unutulmuş şifredir; beşincisi bir örüntüdür. Yönetici
              "açayım mı" kararını buna bakarak verir — o yüzden sayı
              gizlenmiyor, öne çıkarılıyor.
            */
            <Rozet ton="uyari">{hesap.kilitSayisi}. kez kilitlendi</Rozet>
          )}
          {gerekce === null && (
            <Buton gorunum="ikincil" onClick={() => setGerekce("")}>
              Kilidi aç
            </Buton>
          )}
        </div>
      </div>

      {gerekce !== null && (
        <div className="bg-notr-50 rounded-alan flex flex-col gap-2 p-3">
          <Alan
            etiket="Neden açıyorsunuz?"
            yardim={`Denetim kaydına yazılır, en az ${GEREKCE_AGIR_EN_AZ} karakter. "Kim açtı" tek başına yetmiyor; aynı hesabı üç kez açmak normal de olabilir, şüpheli de.`}
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Buton
              onClick={() => ac.mutate()}
              yukleniyor={ac.isPending}
              yukleniyorMetni="Açılıyor"
              /*
                ESIK SOZLESMEDEN. Ekran kendi sayisini yazsaydi,
                sunucununkiyle ayristigi gun ya kabul edilecek bir
                gerekceyi engellerdi ya da reddedilecek birini
                gonderirdi. Bu depoda ayni hata olculmus: panel kendi
                `GEREKCE_EN_AZ = 15`ini tanimlayip sozlesmedekini
                GOLGELEMISTI.
              */
              disabled={gerekce.trim().length < GEREKCE_AGIR_EN_AZ}
            >
              Kilidi aç
            </Buton>
            <Buton gorunum="ikincil" onClick={() => setGerekce(null)}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}

      {ac.isError && (
        <p role="alert" className="text-hata-700 text-sm">
          {ac.error instanceof ApiHatasi ? ac.error.message : "Kilit açılamadı."}
        </p>
      )}
      {sonuc && (
        /*
          `role="status"` — SUSU DEGIL, TEK GERI BILDIRIM.
          Ekran okuyucu kullanicisi icin bu kutu islemin bittigini
          soyleyen tek sey; canli bolge tasimazsa dugmeye basiyor ve
          hicbir sey duymuyor. 04.09.2026 olcumu: yirmi geri bildirim
          kutusundan ucu sessizdi ve `geri-bildirim-duyurusu.test.ts`
          tam bunun icin yazildi — bu dosyayi da yakaladi.
        */
        <p role="status" className="text-notr-700 text-sm">
          {sonuc}
        </p>
      )}
    </div>
  );
}

export function KilitliHesaplar() {
  const liste = useQuery({
    queryKey: ["guvenlik-kilitli"],
    queryFn: kilitliHesaplar,
    retry: false,
  });

  if (liste.isPending) return <Iskelet satir={3} />;

  if (liste.isError) {
    return (
      <Kart baslik="Kilitli hesaplar">
        <BosDurum
          ton="hata"
          baslik="Liste alınamadı"
          aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  const hesaplar = liste.data.hesaplar;

  return (
    <Kart
      baslik="Kilitli hesaplar"
      aciklama="Art arda yanlış şifre girildiği için giriş yapamayan hesaplar. Kilidi açmak denetim kaydına geçer."
    >
      {hesaplar.length === 0 ? (
        <p className="text-notr-700 text-sm">
          Şu an kilitli hesap yok. Bir hesap kilitlendiğinde burada görünür; kilit süresi
          kendiliğinden dolar, beklemek istemeyen kullanıcı için buradan açabilirsiniz.
        </p>
      ) : (
        <div className="flex flex-col">
          {hesaplar.map((h) => (
            <KilitliSatir key={h.userId} hesap={h} />
          ))}
        </div>
      )}
    </Kart>
  );
}

function DenemeSatiri({ deneme }: { deneme: GirisDenemesi }) {
  return (
    <li className="border-notr-200 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b py-2 text-sm last:border-b-0">
      {/*
        "kimlik" — "e-posta" DEĞİL. Bu alan telefon ve MASKELİ T.C. de
        taşıyabiliyor (G-AUT-00). Sütuna "e-posta" demek, T.C. ile giren
        birinin satırını yanlış etiketlerdi. Ham T.C. hiçbir yerde
        gösterilmiyor (F-LOG-12) ve maskeleme YAZMA anında yapılıyor —
        burada ikinci bir maskeleme yok, olsaydı kural iki eve dağılırdı.
      */}
      <span className="text-notr-900 min-w-0 break-all">{deneme.kimlik}</span>
      <span className="text-notr-600 tabular-nums">{deneme.ipAddress ?? "IP yok"}</span>
      <span className="text-notr-600 text-xs tabular-nums">{zaman(deneme.createdAt)}</span>
    </li>
  );
}

export function GirisDenemeleri() {
  const [saat, setSaat] = useState(24);
  const veri = useQuery({
    queryKey: ["guvenlik-denemeler", saat],
    queryFn: () => girisDenemeleri(saat),
    retry: false,
  });

  if (veri.isPending) return <Iskelet satir={4} />;

  if (veri.isError) {
    return (
      <Kart baslik="Başarısız giriş denemeleri">
        <BosDurum
          ton="hata"
          baslik="Denemeler alınamadı"
          aciklama={veri.error instanceof ApiHatasi ? veri.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void veri.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  const { denemeler, ipler } = veri.data;

  return (
    <Kart
      baslik="Başarısız giriş denemeleri"
      aciklama={`Son ${saat} saat. En fazla 500 kayıt gösterilir; aşağıdaki IP özeti de aynı kayıtlardan hesaplanır.`}
      eylem={
        <div className="flex gap-1">
          {[1, 24, 168].map((s) => (
            /*
              `boyut` VERİLMİYOR. "kucuk" diye bir boyut yok — `Buton`
              yalnızca "normal" ve "genis" tanıyor. Küçültmek için yeni
              bir boyut eklemek, dokunma hedefini 44px'in altına
              düşürme riski demekti; tasarım paketi bu yüzden küçük
              düğme tanımlamamış.
            */
            <Buton key={s} gorunum={s === saat ? "birincil" : "ikincil"} onClick={() => setSaat(s)}>
              {s === 1 ? "1 saat" : s === 24 ? "24 saat" : "7 gün"}
            </Buton>
          ))}
        </div>
      }
    >
      {denemeler.length === 0 ? (
        <p className="text-notr-700 text-sm">Bu aralıkta başarısız giriş denemesi yok.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-notr-900 text-sm font-medium">IP yoğunluğu</h3>
            <ul className="mt-2 flex flex-col gap-2">
              {ipler.map((ip) => {
                const yorum = ipYorumu(ip);
                return (
                  <li key={ip.ipAddress} className="bg-notr-50 rounded-alan p-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-notr-900 text-sm break-all">{ip.ipAddress}</span>
                      <span className="text-notr-700 text-sm tabular-nums">
                        {ip.deneme} deneme · {ip.farkliKimlik} farklı kimlik
                      </span>
                    </div>
                    <p
                      className={`mt-1 text-xs ${yorum.tehlikeli ? "text-uyari-800 font-medium" : "text-notr-600"}`}
                    >
                      {yorum.metin}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h3 className="text-notr-900 text-sm font-medium">
              Denemeler <span className="text-notr-600 font-normal">({denemeler.length})</span>
            </h3>
            <ul className="mt-1 max-h-80 overflow-y-auto">
              {denemeler.map((d, i) => (
                <DenemeSatiri key={`${d.createdAt}-${i}`} deneme={d} />
              ))}
            </ul>
          </div>
        </div>
      )}
    </Kart>
  );
}
